import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  IconButton,
  Paper,
  Avatar,
  Button,
  Menu,
  MenuItem,
  Divider,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import FlightTakeoffIcon from "@mui/icons-material/FlightTakeoff";
import PublicIcon from "@mui/icons-material/Public";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import HistoryIcon from "@mui/icons-material/History";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";

import { INITIAL_TRAVEL_REQUESTS } from "./mockData";
import ApproveModal from "./ApproveModal";
import RejectModal from "./RejectModal";
import SuccessScreen from "./SuccessScreen";
import ApprovalHistory from "./ApprovalHistory";
import AttachmentViewerModal from "./AttachmentViewerModal";

/**
 * RequestDetails2 - Modern Mobile Travel Request Details Screen
 * Implemented accurately according to the provided UI specification
 */
const RequestDetail2 = ({
  request: propRequest,
  onBack: propOnBack,
  onOpenApprove: propOnOpenApprove,
  onOpenReject: propOnOpenReject,
  onOpenHistory: propOnOpenHistory,
  onOpenAttachments: propOnOpenAttachments,
  onSwitchView: propOnSwitchView,
}) => {
  const navigate = useNavigate();
  const location = useLocation();

  // Determine current request: from props, location state, or default mock item
  const initialReq =
    propRequest || location.state?.request || INITIAL_TRAVEL_REQUESTS[0];
  const [request, setRequest] = useState(initialReq);

  useEffect(() => {
    if (propRequest) {
      setRequest(propRequest);
    } else if (location.state?.request) {
      setRequest(location.state.request);
    }
  }, [propRequest, location.state]);

  // Standalone state when opened directly as a route
  const [anchorEl, setAnchorEl] = useState(null);
  const [standaloneView, setStandaloneView] = useState("DETAIL"); // "DETAIL" | "SUCCESS" | "HISTORY"
  const [openApproveModal, setOpenApproveModal] = useState(false);
  const [openRejectModal, setOpenRejectModal] = useState(false);
  const [openAttachmentModal, setOpenAttachmentModal] = useState(false);
  const [actionType, setActionType] = useState("Approved");

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleBack = () => {
    if (propOnBack) {
      propOnBack();
    } else {
      navigate(-1);
    }
  };

  const handleApprove = () => {
    if (propOnOpenApprove) {
      propOnOpenApprove();
    } else {
      setOpenApproveModal(true);
    }
  };

  const handleReject = () => {
    if (propOnOpenReject) {
      propOnOpenReject();
    } else {
      setOpenRejectModal(true);
    }
  };

  const handleHistory = () => {
    handleMenuClose();
    if (propOnOpenHistory) {
      propOnOpenHistory();
    } else {
      setStandaloneView("HISTORY");
    }
  };

  const handleAttachments = () => {
    handleMenuClose();
    if (propOnOpenAttachments) {
      propOnOpenAttachments();
    } else {
      setOpenAttachmentModal(true);
    }
  };

  // Standalone confirmation handlers
  const handleConfirmApproval = () => {
    const updated = { ...request, status: "Approved" };
    setRequest(updated);
    setOpenApproveModal(false);
    setActionType("Approved");
    setStandaloneView("SUCCESS");
  };

  const handleConfirmRejection = (reason) => {
    const updated = { ...request, status: "Rejected", comments: reason };
    setRequest(updated);
    setOpenRejectModal(false);
    setActionType("Rejected");
    setStandaloneView("SUCCESS");
  };

  if (!request) return null;

  // If in Standalone Success View
  if (standaloneView === "SUCCESS") {
    return (
      <SuccessScreen
        request={request}
        actionType={actionType}
        onBackToDashboard={() => setStandaloneView("DETAIL")}
      />
    );
  }

  // If in Standalone History View
  if (standaloneView === "HISTORY") {
    return (
      <ApprovalHistory
        request={request}
        onBack={() => setStandaloneView("DETAIL")}
      />
    );
  }

  const fin = request.financialDetails || {
    allowanceUSD: "2,500",
    allowanceLKR: "750,000",
    airTicketLKR: "300,000",
    visaFeesLKR: "45,000",
    othersLKR: "25,000",
  };

  return (
    <Box
      sx={{
        width: "100%",
        minHeight: "100vh",
        backgroundColor: "#EEF3F8",
        display: "flex",
        justifyContent: "center",
        alignItems: "flex-start",
        py: { xs: 0, sm: 2 },
      }}
    >
      {/* Mobile Device Frame Container */}
      <Box
        sx={{
          width: "100%",
          maxWidth: "430px",
          backgroundColor: "#F4F7FB",
          minHeight: { xs: "100vh", sm: "auto" },
          boxShadow: { xs: "none", sm: "0 12px 40px rgba(0, 35, 90, 0.12)" },
          borderRadius: { xs: 0, sm: "32px" },
          display: "flex",
          flexDirection: "column",
          position: "relative",
        }}
      >
        {/* Top Header Bar */}
        <Box
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 10,
            background: "linear-gradient(180deg, #0056C6 0%, #0047AB 100%)",
            color: "#ffffff",
            px: 2,
            pt: 2.2,
            pb: 2.4,
            borderBottomLeftRadius: 24,
            borderBottomRightRadius: 24,
            boxShadow: "0 4px 18px rgba(0, 75, 185, 0.28)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <IconButton
              onClick={handleBack}
              sx={{
                color: "#ffffff",
                p: 0.8,
                "&:hover": { backgroundColor: "rgba(255,255,255,0.15)" },
              }}
            >
              <ArrowBackIcon sx={{ fontSize: 25 }} />
            </IconButton>

            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                fontSize: "1.15rem",
                color: "#ffffff",
                letterSpacing: "-0.01em",
                textAlign: "center",
                flex: 1,
              }}
            >
              Travel Request Details
            </Typography>

            <IconButton
              onClick={handleMenuClick}
              sx={{
                color: "#ffffff",
                p: 0.8,
                "&:hover": { backgroundColor: "rgba(255,255,255,0.15)" },
              }}
            >
              <MoreVertIcon sx={{ fontSize: 25 }} />
            </IconButton>

            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              PaperProps={{
                sx: {
                  borderRadius: "16px",
                  minWidth: "200px",
                  boxShadow: "0 10px 28px rgba(0,0,0,0.12)",
                },
              }}
            >
              <MenuItem onClick={handleHistory} sx={{ gap: 1.2, fontSize: "0.9rem" }}>
                <HistoryIcon sx={{ fontSize: 20, color: "#0056C6" }} />
                Approval History
              </MenuItem>
              <MenuItem onClick={handleAttachments} sx={{ gap: 1.2, fontSize: "0.9rem" }}>
                <AttachFileIcon sx={{ fontSize: 20, color: "#0056C6" }} />
                Attachments ({request.attachments?.length || 0})
              </MenuItem>
              {propOnSwitchView && (
                <>
                  <Divider />
                  <MenuItem
                    onClick={() => {
                      handleMenuClose();
                      propOnSwitchView();
                    }}
                    sx={{ gap: 1.2, fontSize: "0.9rem" }}
                  >
                    <SwapHorizIcon sx={{ fontSize: 20, color: "#64748B" }} />
                    Switch to Classic Detail
                  </MenuItem>
                </>
              )}
            </Menu>
          </Box>
        </Box>

        {/* Content Body */}
        <Box
          sx={{
            p: 2,
            display: "flex",
            flexDirection: "column",
            gap: 1.8,
            pb: 2.5,
          }}
        >
          {/* 1. Employee Profile Card */}
          <Paper
            elevation={0}
            sx={{
              backgroundColor: "#ffffff",
              borderRadius: "20px",
              p: 2,
              border: "1px solid #E3EAF2",
              boxShadow: "0 2px 10px rgba(10, 30, 65, 0.04)",
              display: "flex",
              alignItems: "center",
              position: "relative",
            }}
          >
            <Avatar
              src={request.avatar}
              alt={request.name}
              sx={{
                width: 76,
                height: 76,
                borderRadius: "50%",
                mr: 2,
                border: "2px solid #EDF2F7",
                boxShadow: "0 4px 12px rgba(0,0,0,0.06)",
              }}
            />

            <Box sx={{ flex: 1, pr: 2 }}>
              <Typography
                sx={{
                  fontWeight: 800,
                  fontSize: "1.15rem",
                  color: "#0A1E3F",
                  lineHeight: 1.25,
                }}
              >
                {request.name}
              </Typography>

              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: "0.92rem",
                  color: "#0A2540",
                  mt: 0.5,
                }}
              >
                Service No : {request.serviceNo || request.employeeNo}
              </Typography>

              <Typography
                sx={{
                  fontWeight: 500,
                  fontSize: "0.85rem",
                  color: "#5B708B",
                  mt: 0.3,
                }}
              >
                {request.department}
              </Typography>

              <Typography
                sx={{
                  fontWeight: 500,
                  fontSize: "0.85rem",
                  color: "#355882",
                  mt: 0.2,
                }}
              >
                {request.role}
              </Typography>
            </Box>

            {/* Top right corner decorative chevron */}
            <Box
              sx={{
                position: "absolute",
                top: 16,
                right: 16,
                color: "#0056C6",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#0056C6"
                strokeWidth="3.2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </Box>
          </Paper>

          {/* 2. Travel Details Card */}
          <Paper
            elevation={0}
            sx={{
              backgroundColor: "#ffffff",
              borderRadius: "20px",
              p: 2.2,
              border: "1px solid #E3EAF2",
              boxShadow: "0 2px 10px rgba(10, 30, 65, 0.04)",
            }}
          >
            {/* Card Header */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 2 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#0A2540",
                }}
              >
                <FlightTakeoffIcon sx={{ fontSize: 23, color: "#0A2540" }} />
              </Box>
              <Typography
                sx={{
                  fontWeight: 800,
                  fontSize: "1.05rem",
                  color: "#0A1E3F",
                }}
              >
                Travel Details
              </Typography>
            </Box>

            {/* Key-Value Details */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.8 }}>
              {/* Country */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
                  <PublicIcon sx={{ fontSize: 20, color: "#0047AB" }} />
                  <Typography
                    sx={{ color: "#475569", fontSize: "0.92rem", fontWeight: 500 }}
                  >
                    Country
                  </Typography>
                </Box>
                <Typography
                  sx={{ color: "#0A1E3F", fontSize: "0.95rem", fontWeight: 600 }}
                >
                  {request.country}
                </Typography>
              </Box>

              {/* Departure Date */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
                  <CalendarMonthOutlinedIcon
                    sx={{ fontSize: 20, color: "#0047AB" }}
                  />
                  <Typography
                    sx={{ color: "#475569", fontSize: "0.92rem", fontWeight: 500 }}
                  >
                    Departure Date
                  </Typography>
                </Box>
                <Typography
                  sx={{ color: "#0A1E3F", fontSize: "0.95rem", fontWeight: 600 }}
                >
                  {request.departureDate}
                </Typography>
              </Box>

              {/* Arrival Date */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
                  <CalendarMonthOutlinedIcon
                    sx={{ fontSize: 20, color: "#0047AB" }}
                  />
                  <Typography
                    sx={{ color: "#475569", fontSize: "0.92rem", fontWeight: 500 }}
                  >
                    Arrival Date
                  </Typography>
                </Box>
                <Typography
                  sx={{ color: "#0A1E3F", fontSize: "0.95rem", fontWeight: 600 }}
                >
                  {request.arrivalDate || request.returnDate}
                </Typography>
              </Box>

              {/* Duration */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
                  <AccessTimeIcon sx={{ fontSize: 20, color: "#0047AB" }} />
                  <Typography
                    sx={{ color: "#475569", fontSize: "0.92rem", fontWeight: 500 }}
                  >
                    Duration
                  </Typography>
                </Box>
                <Typography
                  sx={{ color: "#0A1E3F", fontSize: "0.95rem", fontWeight: 600 }}
                >
                  {request.duration}
                </Typography>
              </Box>
            </Box>
          </Paper>

          {/* 3. Purpose Card */}
          <Paper
            elevation={0}
            sx={{
              backgroundColor: "#ffffff",
              borderRadius: "20px",
              p: 2.2,
              border: "1px solid #E3EAF2",
              boxShadow: "0 2px 10px rgba(10, 30, 65, 0.04)",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 1.6 }}>
              <DescriptionOutlinedIcon sx={{ fontSize: 23, color: "#0A2540" }} />
              <Typography
                sx={{
                  fontWeight: 800,
                  fontSize: "1.05rem",
                  color: "#0A1E3F",
                }}
              >
                Purpose
              </Typography>
            </Box>

            <Box
              sx={{
                backgroundColor: "#EEF4FA",
                borderRadius: "14px",
                p: "14px 16px",
                border: "1px solid #E1EAF2",
              }}
            >
              <Typography
                sx={{
                  color: "#1E3A5F",
                  fontSize: "0.92rem",
                  lineHeight: 1.45,
                  fontWeight: 500,
                }}
              >
                {request.purpose}
              </Typography>
            </Box>
          </Paper>

          {/* 4. Financial Details Card */}
          <Paper
            elevation={0}
            sx={{
              backgroundColor: "#ffffff",
              borderRadius: "20px",
              p: 2.2,
              border: "1px solid #E3EAF2",
              boxShadow: "0 2px 10px rgba(10, 30, 65, 0.04)",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 2 }}>
              <AccountBalanceWalletIcon sx={{ fontSize: 23, color: "#0A2540" }} />
              <Typography
                sx={{
                  fontWeight: 800,
                  fontSize: "1.05rem",
                  color: "#0A1E3F",
                }}
              >
                Financial Details
              </Typography>
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {/* Allowance */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mt: 0.3 }}>
                  <AccountBalanceWalletOutlinedIcon
                    sx={{ fontSize: 20, color: "#0047AB" }}
                  />
                  <Typography
                    sx={{ color: "#475569", fontSize: "0.92rem", fontWeight: 500 }}
                  >
                    Allowance
                  </Typography>
                </Box>
                <Box sx={{ textAlign: "right" }}>
                  <Typography
                    sx={{
                      color: "#0A1E3F",
                      fontSize: "1.05rem",
                      fontWeight: 800,
                    }}
                  >
                    USD : {fin.allowanceUSD}
                  </Typography>
                  <Typography
                    sx={{
                      color: "#1E293B",
                      fontSize: "0.92rem",
                      fontWeight: 500,
                      mt: 0.3,
                    }}
                  >
                    Rs : {fin.allowanceLKR}
                  </Typography>
                </Box>
              </Box>

              {/* Air ticket */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
                  <FlightTakeoffIcon sx={{ fontSize: 20, color: "#0047AB" }} />
                  <Typography
                    sx={{ color: "#475569", fontSize: "0.92rem", fontWeight: 500 }}
                  >
                    Air ticket
                  </Typography>
                </Box>
                <Typography
                  sx={{ color: "#0A1E3F", fontSize: "0.95rem", fontWeight: 600 }}
                >
                  Rs : {fin.airTicketLKR}
                </Typography>
              </Box>

              {/* Visa fees */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
                  <ReceiptLongOutlinedIcon sx={{ fontSize: 20, color: "#0047AB" }} />
                  <Typography
                    sx={{ color: "#475569", fontSize: "0.92rem", fontWeight: 500 }}
                  >
                    Visa fees
                  </Typography>
                </Box>
                <Typography
                  sx={{ color: "#0A1E3F", fontSize: "0.95rem", fontWeight: 600 }}
                >
                  Rs : {fin.visaFeesLKR}
                </Typography>
              </Box>

              {/* Others */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
                  <MoreHorizIcon sx={{ fontSize: 20, color: "#0047AB" }} />
                  <Typography
                    sx={{ color: "#475569", fontSize: "0.92rem", fontWeight: 500 }}
                  >
                    Others
                  </Typography>
                </Box>
                <Typography
                  sx={{ color: "#0A1E3F", fontSize: "0.95rem", fontWeight: 600 }}
                >
                  Rs : {fin.othersLKR}
                </Typography>
              </Box>
            </Box>
          </Paper>

          {/* 5. Bottom Action Buttons */}
          {request?.status?.toLowerCase() === "pending" && (
            <Box
              sx={{
                position: "sticky",
                bottom: 12,
                zIndex: 5,
                width: "100%",
                mt: 1,
              }}
            >
              <Paper
                elevation={4}
                sx={{
                  width: "100%",
                  p: 1.5,
                  borderRadius: "18px",
                  backgroundColor: "#ffffff",
                  boxShadow: "0 6px 24px rgba(0,0,0,0.12)",
                  border: "1px solid #E2E8F0",
                  display: "flex",
                  gap: 1.5,
                }}
              >
                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleReject}
                  startIcon={<CloseIcon />}
                  sx={{
                    backgroundColor: "#EF4444",
                    color: "#ffffff",
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "0.95rem",
                    borderRadius: "12px",
                    py: 1.2,
                    boxShadow: "0 4px 12px rgba(239, 68, 68, 0.25)",
                    "&:hover": { backgroundColor: "#DC2626" },
                  }}
                >
                  Reject
                </Button>

                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleApprove}
                  startIcon={<CheckIcon />}
                  sx={{
                    backgroundColor: "#16A34A",
                    color: "#ffffff",
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "0.95rem",
                    borderRadius: "12px",
                    py: 1.2,
                    boxShadow: "0 4px 12px rgba(22, 163, 74, 0.25)",
                    "&:hover": { backgroundColor: "#15803D" },
                  }}
                >
                  Approve
                </Button>
              </Paper>
            </Box>
          )}
        </Box>
      </Box>

      {/* Standalone Modals (only active if used directly via route) */}
      <ApproveModal
        open={openApproveModal}
        onClose={() => setOpenApproveModal(false)}
        onConfirm={handleConfirmApproval}
        employeeName={request?.name}
      />

      <RejectModal
        open={openRejectModal}
        onClose={() => setOpenRejectModal(false)}
        onConfirm={handleConfirmRejection}
        employeeName={request?.name}
      />

      <AttachmentViewerModal
        open={openAttachmentModal}
        onClose={() => setOpenAttachmentModal(false)}
        attachments={request?.attachments || []}
      />
    </Box>
  );
};

export default RequestDetail2;
